import { useVirtualizer } from '@tanstack/react-virtual';
import { BellOff, Check, ExternalLink, MoreHorizontal, Undo2, X } from 'lucide-react';
import { useEffect, useRef, type CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router';
import { Avatar, SeverityBadge, StateBadge } from '../../components/ui/Badge.js';
import { Menu, MenuItem } from '../../components/ui/Menu.js';
import { RelativeTime } from '../../components/ui/Time.js';
import { cx } from '../../lib/cx.js';
import { formatCount } from '../../lib/format.js';
import type { Alarm } from '../../lib/schemas.js';
import type { Directory } from '../../lib/directory.js';
import { SEVERITY_TOKENS } from '../../lib/severity.js';
import {
  ALARM_COLUMNS,
  ROW_HEIGHT,
  VIRTUALIZE_THRESHOLD,
  type AlarmColumnKey,
  type Density,
} from './columns.js';

export interface AlarmTableProps {
  alarms: Alarm[];
  /** Resolves the integration and assignee ids a list row carries. */
  directory: Directory;
  selected: Set<string>;
  onToggle: (id: string, shiftKey: boolean) => void;
  onToggleAll: () => void;
  visibleColumns: Set<AlarmColumnKey>;
  density: Density;
  /** Index of the row the keyboard is on (j/k). */
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  /** Ids inserted by the live feed since the last flush; flashed once. */
  highlighted?: Set<string>;
  onAck: (alarm: Alarm) => void;
  onClose: (alarm: Alarm) => void;
  onSnooze: (alarm: Alarm) => void;
  onUnack: (alarm: Alarm) => void;
  /** Off inside a group, so the column header is printed once for the page. */
  showHeader?: boolean;
  className?: string;
}

/**
 * Dense alarm table. Severity strip on the left, state pill, entity, title,
 * source, count, age (relative with the exact time on hover) and assignee.
 * Rows are selectable, keyboard navigable and virtualised past 60 rows.
 */
export function AlarmTable({
  alarms,
  directory,
  selected,
  onToggle,
  onToggleAll,
  visibleColumns,
  density,
  activeIndex,
  onActiveIndexChange,
  highlighted,
  onAck,
  onClose,
  onSnooze,
  onUnack,
  showHeader = true,
  className,
}: AlarmTableProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const rowHeight = ROW_HEIGHT[density];
  const shouldVirtualize = alarms.length > VIRTUALIZE_THRESHOLD;

  const virtualizer = useVirtualizer({
    count: shouldVirtualize ? alarms.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
  });

  useEffect(() => {
    if (!shouldVirtualize) return;
    if (activeIndex < 0 || activeIndex >= alarms.length) return;
    virtualizer.scrollToIndex(activeIndex, { align: 'auto' });
  }, [activeIndex, alarms.length, shouldVirtualize, virtualizer]);

  const columns = ALARM_COLUMNS.filter((column) => visibleColumns.has(column.key));
  const gridTemplate = `32px ${columns.map((c) => c.width).join(' ')} 40px`;
  const allSelected = alarms.length > 0 && alarms.every((alarm) => selected.has(alarm.id));

  const renderRow = (alarm: Alarm, index: number, style?: CSSProperties) => (
    <AlarmRow
      key={alarm.id}
      alarm={alarm}
      directory={directory}
      index={index}
      style={style}
      gridTemplate={gridTemplate}
      columns={columns.map((c) => c.key)}
      rowHeight={rowHeight}
      selected={selected.has(alarm.id)}
      active={index === activeIndex}
      flash={highlighted?.has(alarm.id) ?? false}
      onToggle={onToggle}
      onFocusRow={onActiveIndexChange}
      onAck={onAck}
      onClose={onClose}
      onSnooze={onSnooze}
      onUnack={onUnack}
    />
  );

  return (
    <div
      className={cx(
        'overflow-hidden rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)]',
        className,
      )}
    >
      {showHeader && (
        <div
          role="row"
          className="grid items-center gap-2 border-b border-[var(--border)] bg-[var(--surface-2)] px-2 py-1.5"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          <span className="flex justify-center">
            <input
              type="checkbox"
              aria-label="Select all alarms"
              checked={allSelected}
              onChange={onToggleAll}
              className="h-3.5 w-3.5 accent-[var(--accent)]"
            />
          </span>
          {columns.map((column) => (
            <span
              key={column.key}
              role="columnheader"
              className="truncate text-[var(--text-sm)] font-medium text-[var(--text-3)]"
            >
              {column.label}
            </span>
          ))}
          <span aria-hidden />
        </div>
      )}

      <div
        ref={scrollRef}
        role="grid"
        aria-label="Alarms"
        aria-rowcount={alarms.length}
        className={cx('relative', shouldVirtualize && 'max-h-[calc(100vh-320px)] overflow-y-auto')}
      >
        {shouldVirtualize ? (
          <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
            {virtualizer.getVirtualItems().map((item) =>
              renderRow(alarms[item.index]!, item.index, {
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${item.start}px)`,
                height: item.size,
              }),
            )}
          </div>
        ) : (
          alarms.map((alarm, index) => renderRow(alarm, index))
        )}
      </div>
    </div>
  );
}

interface AlarmRowProps {
  alarm: Alarm;
  directory: Directory;
  index: number;
  style?: CSSProperties;
  gridTemplate: string;
  columns: AlarmColumnKey[];
  rowHeight: number;
  selected: boolean;
  active: boolean;
  flash: boolean;
  onToggle: (id: string, shiftKey: boolean) => void;
  onFocusRow: (index: number) => void;
  onAck: (alarm: Alarm) => void;
  onClose: (alarm: Alarm) => void;
  onSnooze: (alarm: Alarm) => void;
  onUnack: (alarm: Alarm) => void;
}

function AlarmRow({
  alarm,
  directory,
  index,
  style,
  gridTemplate,
  columns,
  rowHeight,
  selected,
  active,
  flash,
  onToggle,
  onFocusRow,
  onAck,
  onClose,
  onSnooze,
  onUnack,
}: AlarmRowProps) {
  const navigate = useNavigate();
  const sourceName =
    alarm.integration?.name ?? directory.integrationName(alarm.integration_id) ?? '—';
  const assigneeName =
    alarm.assignee?.display_name ?? directory.userName(alarm.assignee_id) ?? null;
  const cells: Record<AlarmColumnKey, React.ReactNode> = {
    severity: <SeverityBadge severity={alarm.severity} />,
    state: <StateBadge state={alarm.state} suppressed={alarm.suppressed} />,
    entity: (
      <span className="truncate text-[var(--text-2)]" title={alarm.entity.name}>
        {alarm.entity.name || '—'}
      </span>
    ),
    title: (
      <span className="flex min-w-0 items-center gap-1.5">
        <Link
          to={`/alarms/${alarm.id}`}
          className="min-w-0 truncate font-medium text-[var(--text)] hover:underline"
          title={alarm.title}
        >
          {alarm.title}
        </Link>
        {alarm.flapping && (
          <span
            className="shrink-0 text-[var(--text-sm)] text-[var(--warn)]"
            title="Flapping: repeated open/close cycles"
          >
            flapping
          </span>
        )}
      </span>
    ),
    source: (
      <span className="truncate text-[var(--text-2)]" title={sourceName}>
        {sourceName}
      </span>
    ),
    count: (
      <span className="tnum text-right text-[var(--text-2)]">
        {formatCount(alarm.occurrence_count)}
      </span>
    ),
    age: <RelativeTime value={alarm.last_seen} className="text-[var(--text-2)]" />,
    assignee: <Avatar name={assigneeName} title={assigneeName ?? 'Unassigned'} />,
  };

  return (
    <div
      role="row"
      aria-rowindex={index + 1}
      aria-selected={selected}
      tabIndex={-1}
      style={{ ...style, gridTemplateColumns: gridTemplate, minHeight: rowHeight }}
      onMouseEnter={() => onFocusRow(index)}
      className={cx(
        'group relative grid items-center gap-2 border-b border-[var(--border)] px-2 text-[var(--text-base)]',
        'transition-colors duration-[var(--dur-fast)]',
        selected
          ? 'bg-[var(--accent-soft)]'
          : active
            ? 'bg-[var(--surface-2)]'
            : 'bg-[var(--surface)] hover:bg-[var(--surface-2)]',
        flash && 'vam-flash',
      )}
    >
      <span
        aria-hidden
        className="absolute left-0 top-0 h-full w-[3px]"
        style={{ backgroundColor: SEVERITY_TOKENS[alarm.severity].ink }}
      />
      <span className="flex justify-center">
        <input
          type="checkbox"
          checked={selected}
          aria-label={`Select ${alarm.title}`}
          onChange={(event) =>
            onToggle(alarm.id, (event.nativeEvent as MouseEvent).shiftKey === true)
          }
          className="h-3.5 w-3.5 accent-[var(--accent)]"
        />
      </span>
      {columns.map((key) => (
        <span key={key} role="gridcell" className="flex min-w-0 items-center">
          {cells[key]}
        </span>
      ))}
      <span className="flex justify-end opacity-0 transition-opacity duration-[var(--dur-fast)] focus-within:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100">
        <Menu
          label={`Actions for ${alarm.title}`}
          align="end"
          triggerClassName="h-6 w-6 justify-center rounded-[var(--radius-sm)] text-[var(--text-3)] hover:bg-[var(--surface-3)]"
          trigger={<MoreHorizontal className="h-4 w-4" />}
        >
          {(close) => (
            <>
              {alarm.state === 'acknowledged' ? (
                <MenuItem
                  icon={<Undo2 className="h-3.5 w-3.5" />}
                  onClick={() => {
                    onUnack(alarm);
                    close();
                  }}
                >
                  Un-acknowledge
                </MenuItem>
              ) : (
                <MenuItem
                  icon={<Check className="h-3.5 w-3.5" />}
                  onClick={() => {
                    onAck(alarm);
                    close();
                  }}
                >
                  Acknowledge
                </MenuItem>
              )}
              <MenuItem
                icon={<BellOff className="h-3.5 w-3.5" />}
                onClick={() => {
                  onSnooze(alarm);
                  close();
                }}
              >
                Snooze…
              </MenuItem>
              <MenuItem
                icon={<X className="h-3.5 w-3.5" />}
                onClick={() => {
                  onClose(alarm);
                  close();
                }}
              >
                Close alarm
              </MenuItem>
              <MenuItem
                icon={<ExternalLink className="h-3.5 w-3.5" />}
                onClick={() => {
                  close();
                  void navigate(`/alarms/${alarm.id}`);
                }}
              >
                Open detail
              </MenuItem>
            </>
          )}
        </Menu>
      </span>
    </div>
  );
}
